import { classnames } from 'tailwindcss-classnames';
import NrcMenuClose from 'components/authorable/site/Header/SvgMenuClose/nrc-menu-close';
import NrcMenu from 'components/authorable/site/Header/SvgMenu/nrc-menu';
import NextLink from 'next/link';
import NrcSearch from 'components/authorable/site/Header/SvgSearch/nrc-search';
import { CollectionStackItem } from './NmdpSiteHeaderTypes';
import { pageorLink } from 'components/authorable/site/Footer/Footer';
import { useCartContext } from 'components/globalContext/CartContext';
import LinkA11yWrapper from 'components/helpers/LinkA11yWrapper';
import Button from 'components/helpers/Button';
import { menuLocation, pageNavigationDataLayerHandler } from 'lib/util/gtm-datalayer';
import Search from 'components/authorable/site/Header/SvgSearch/search';
import {
  ImageField,
  LinkField,
  Image as SitecoreImage,
  useSitecoreContext,
  Field,
} from '@sitecore-jss/sitecore-jss-react';
import {
  hasSubmenu,
  utilityLinkClass,
  animateNavOpen,
  animateNavClose,
  mobileMainNav,
  toggleButton,
} from './NmdpSiteHeaderClasses';

type MobileTopContainerProps = {
  toggleMobileMenu: () => void;
  toggleSearchMobileMenu: () => void;
  closeSearch: () => void;
  isOpen: boolean;
  searchIsOpen: boolean;
  logo: ImageField;
  logoText: string;
  giveMoneyButton: LinkField;
  joinRegistryButton: LinkField;
  searchPage: boolean;
  isSmallMobileScreen: boolean;
};

type MobileNavigationProps = {
  isOpen: boolean;
  collectionStack: CollectionStackItem[];
  getBackButton: () => React.ReactNode;
  mobileItems: React.ReactNode;
  searchIsOpen: boolean;
  placeholderText: string;
  utilityLinks: pageorLink[];
};

type MobileBottomContainerProps = {
  giveMoneyButton: LinkField;
  giveButtonMobileLabel?: string;
  joinRegistryButton: LinkField;
  joinButtonMobileLabel?: string;
};

const buttonWrapper = classnames('px-2', 'flex-grow');

export const MobileNavigation = ({
  isOpen,
  collectionStack,
  getBackButton,
  mobileItems,
  searchIsOpen,
  placeholderText,
  utilityLinks,
}: MobileNavigationProps) => {
  const fields = collectionStack[1]?.fields;
  const { sitecoreContext } = useSitecoreContext();
  const cartLink = sitecoreContext?.cartLink as LinkField;
  const { cartContext } = useCartContext();
  const cartLinkwithCount = {
    ...cartLink?.value,
    text: `${cartLink?.value?.text || ''} (${cartContext})`,
  };

  return (
    <>
      <nav
        className={classnames(
          mobileMainNav,
          isOpen ? animateNavOpen : animateNavClose,
          collectionStack.length > 1 ? 'state-open' : null
        )}
      >
        {getBackButton()}
        {collectionStack.length === 2 && fields && (
          <>
            {fields?.navigationLink && (
              <div className={classnames('max-h-[25px]', 'p-4')}>
                <LinkA11yWrapper
                  editable={false}
                  field={fields.navigationLink}
                  className={classnames('text-blue', 'font-medium')}
                />
              </div>
            )}
            {fields?.description?.value && (
              <p
                className={classnames(
                  'text-gray-darkGray',
                  'py-2',
                  'mx-4',
                  'my-2',
                  'border-b',
                  'border-gray-whisper'
                )}
              >
                {fields?.description?.value}
              </p>
            )}
          </>
        )}

        <ul role="menubar">{mobileItems}</ul>
        <ul>
          {utilityLinks?.map((item) => (
            <li role="menuitem" key={item.name} className={hasSubmenu} aria-label="Menu item">
              <LinkA11yWrapper
                className={classnames(utilityLinkClass, 'px-4', 'py-7', 'font-medium')}
                editable={false}
                suppressNewTabIcon={false}
                field={
                  item.fields.navigationItemLink
                    ? (item.fields.navigationItemLink as LinkField)
                    : {
                        value: {
                          href: item.url,
                          text: (item.fields.pageTitle as Field<string>).value
                            ? (item.fields.pageTitle as Field<string>).value
                            : item.displayName,
                        },
                      }
                }
                onGtmPush={(e) => {
                  pageNavigationDataLayerHandler(e, menuLocation[0]);
                }}
              />
            </li>
          ))}
        </ul>
      </nav>
      <ul
        role="menu"
        className={classnames(
          'flex',
          'flex-col',
          'w-full',
          'px-4',
          'text-white',
          'justify-end',
          'border-t',
          'border-gray-midGray',
          'fixed',
          'bottom-0',
          'bg-white',
          'z-40',
          isOpen ? animateNavOpen : animateNavClose,
          collectionStack.length > 1 ? 'state-open' : null
        )}
      >
        {cartContext > 0 && cartLink?.value?.href?.length && (
          <li role="menuitem" className={classnames('py-3')} aria-label="Menu item">
            <LinkA11yWrapper
              field={{ value: cartLinkwithCount }}
              className={classnames(utilityLinkClass)}
            />
          </li>
        )}
      </ul>
      <div
        className={classnames(
          searchIsOpen ? animateNavOpen : animateNavClose,
          'aa-IconWrapper',
          'absolute',
          'h-full',
          'transition-transform',
          'w-full',
          'xl:hidden',
          'z-30',
          'mobile-nav-search',
          'top-[90px]'
        )}
      >
        <Search searchplaceholderText={placeholderText} />
        <div className={classnames('bg-black', 'h-full', 'opacity-50')} />
      </div>
    </>
  );
};

const mobileBtnClass = classnames('sm:px-10', 'text-center', 'w-full');

export const MobileTopContainer = ({
  toggleMobileMenu,
  toggleSearchMobileMenu,
  closeSearch,
  isOpen,
  searchIsOpen,
  logo,
  logoText,
  giveMoneyButton,
  joinRegistryButton,
  searchPage,
  isSmallMobileScreen,
}: MobileTopContainerProps): JSX.Element => {
  return (
    <div className={classnames('xl:block', 'border-blue-navy')}>
      <div
        className={classnames(
          'flex',
          'justify-between',
          'md:justify-start',
          'items-center',
          'sm:px-2',
          'sm:py-3',
          'py-2',
          'border-b-2',
          'border-gray-whisper'
        )}
      >
        <div className={classnames('self-start')}>
          <button
            type="button"
            aria-label="Toggle navigation menu"
            className={classnames(toggleButton)}
            data-id="header-mobile-menu-toggle"
            onClick={toggleMobileMenu}
          >
            <div
              className={classnames(
                'align-middle',
                'flex',
                'flex-col-reverse',
                'hover:text-blue-navy',
                'justify-between',
                'text-blue',
                'text-body',
                'w-full'
              )}
            >
              <span
                className={classnames('leading-2', 'px-2.5', 'pt-1', 'text-blue-navy', 'text-xs')}
              >
                {isOpen ? 'Close' : 'Menu'}
              </span>
              {isOpen ? (
                <NrcMenuClose
                  className={classnames('mx-auto')}
                  style={{ width: '18px', height: '18px' }}
                />
              ) : (
                <NrcMenu
                  className={classnames('mx-auto')}
                  style={{ width: '28px', height: '24px' }}
                />
              )}
            </div>
          </button>
        </div>
        <div className={classnames('self-start', 'flex')}>
          <NextLink href="/" locale={false}>
            <a onClick={closeSearch} href="/" aria-label="Logo link">
              <SitecoreImage field={logo} className={classnames('h-[65px]', 'w-[161px]')} />
            </a>
          </NextLink>
          {logoText !== '' && (
            <div className={classnames('flex', 'items-center', 'ml-1')}>
              <NextLink href="/" locale={false}>
                <a
                  onClick={closeSearch}
                  href="/"
                  className={classnames(
                    'text-blue-navy',
                    'font-bold',
                    'font-dmSans',
                    'text-m',
                    'sm:text-xl'
                  )}
                >
                  {logoText}
                </a>
              </NextLink>
            </div>
          )}
        </div>
        <div className={classnames('md:ml-auto', 'flex', 'items-center')}>
          {!isSmallMobileScreen && (
            <>
              {giveMoneyButton?.value && (
                <div className={classnames('pr-3')}>
                  <Button linkField={giveMoneyButton} className={mobileBtnClass} />
                </div>
              )}
              {joinRegistryButton?.value && (
                <div className={classnames('pr-4')}>
                  <Button linkField={joinRegistryButton} className={mobileBtnClass} />
                </div>
              )}
            </>
          )}

          <div className={classnames('self-start')}>
            <button
              type="button"
              aria-label="Search"
              className={toggleButton}
              data-id="Search"
              onClick={toggleSearchMobileMenu}
            >
              <div
                className={classnames(
                  'align-middle',
                  'flex',
                  'flex-col-reverse',
                  'hover:text-blue-navy',
                  'justify-between',
                  'pr-1',
                  'text-blue',
                  'text-body',
                  'w-full',
                  { invisible: searchPage || searchIsOpen }
                )}
              >
                <span className={classnames('px-2.5', 'leading-2', 'text-xs', 'text-blue-navy')}>
                  Search
                </span>
                <NrcSearch className={classnames('mx-auto')} />
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const getButtonFields = (field: LinkField, label?: string) => {
  if (!label) {
    return field;
  }
  return {
    ...field,
    value: {
      ...field.value,
      text: label,
      title: label,
    },
  };
};

export const MobileBottomContainer = ({
  giveMoneyButton,
  giveButtonMobileLabel,
  joinRegistryButton,
  joinButtonMobileLabel,
}: MobileBottomContainerProps): JSX.Element => {
  return (
    <div
      className={classnames(
        'flex',
        'justify-center',
        'items-center',
        'px-6',
        'py-4',
        'bg-gray-whisper'
      )}
    >
      {giveMoneyButton?.value && (
        <div className={buttonWrapper}>
          <Button
            linkField={getButtonFields(giveMoneyButton, giveButtonMobileLabel)}
            className={mobileBtnClass}
          />
        </div>
      )}
      {joinRegistryButton?.value && (
        <div className={buttonWrapper}>
          <Button
            linkField={getButtonFields(joinRegistryButton, joinButtonMobileLabel)}
            className={mobileBtnClass}
          />
        </div>
      )}
    </div>
  );
};
